/**
 * Zod schema for a MATH template's document — an ordered set of auto-graded exercises. Client-side
 * only: the backend stores `data` blind (RND-196 decision 7).
 *
 * Reverses RND-60 decision 4 for MATH: the template `type` selects the schema (`documentSchemaFor`).
 * The prompt reuses the shared `TextBlock` / `ImageBlock` from `lib/assessments/schema.ts` (text may
 * carry `$…$` KaTeX); nothing else is shared. Own `MATH_PRACTICE_SCHEMA_VERSION`, independent of the
 * reading and assessment versions. Stays in app-admin until app-student becomes a second consumer
 * (decision 12).
 *
 * Answer keys are canonical (decision 8): multiple choice and number line key by id, pairs are
 * stored aligned, order items in the correct order. The player shuffles.
 *
 * Issue paths are document-rooted at the document level (`definition.exercises.2.options.0.text`),
 * like the reading schema's.
 */
import { z } from 'zod'

import { Grade, MathExerciseType } from '../../model/assignments/enums'
import { ImageBlock } from '../assessments/schema'
import { TextBlock } from '../assessments/schema'

export const MATH_PRACTICE_SCHEMA_VERSION = 1

const MATH_EXERCISE_DIFFICULTIES = [1, 2, 3] as const

export const MATH_CHOICE_MIN_OPTIONS = 2
export const MATH_PAIRS_MIN = 2
export const MATH_ORDER_MIN_ITEMS = 2
export const MATH_NUMBER_LINE_MIN_TICKS = 2
export const MATH_MULTI_STEP_MIN_STEPS = 2
export const MATH_MULTI_STEP_MAX_STEPS = 4

/**
 * Every character the student keypad can produce — digits, `-`, `.`, `/` and nothing else, no
 * space. Mirrors app-student's `isTypeableAnswer` (`features/math-practice/model/answer-input.ts`)
 * exactly, so every authored answer is playable (decision 9). Duplicated until the schema moves to
 * lib-core-types; change both or neither.
 */
const TYPEABLE = /^[\d./-]+$/

export const isTypeableAnswer = (answer: string): boolean => TYPEABLE.test(answer.trim())

/**
 * Checked, not transformed: validation must not rewrite what the store saves. The same message
 * covers a missing or `null` value, so an unfilled field never reports Zod's raw type error.
 */
const requiredText = (message: string) =>
  z.string({ error: message }).refine(value => value.trim().length > 0, { message })

const ANSWER_REQUIRED_MESSAGE = 'The answer is required.'
const CORRECT_OPTION_MESSAGE = 'Mark the correct option.'
const CORRECT_TICK_MESSAGE = 'Mark the correct tick.'

const typeableAnswer = z.string({ error: ANSWER_REQUIRED_MESSAGE }).superRefine((value, ctx) => {
  if (value.trim().length === 0) {
    ctx.addIssue({ code: 'custom', message: ANSWER_REQUIRED_MESSAGE })
    return
  }
  if (!isTypeableAnswer(value)) {
    ctx.addIssue({
      code: 'custom',
      message: 'The answer must be typeable on the kid\'s keypad: digits, "-", "." and "/" only, no spaces.',
    })
  }
})

/** A prompt is text and images only — no audio. Empty blocks are an authoring gap, not a prompt. */
const MathPromptBlock = z.discriminatedUnion('type', [
  TextBlock.extend({ value: requiredText('The text is empty.') }),
  ImageBlock.extend({ asset: z.object({ url: requiredText('Upload an image or paste its URL.') }) }),
], { error: 'A prompt block must be text or an image.' })

const ExerciseBase = {
  id: z.string().min(1, 'The exercise has no id.'),
  prompt: z.array(MathPromptBlock).min(1, 'An exercise needs a prompt.'),
  hint: z.string().optional(),
  explanation: z.string().optional(),
  /** Not authored in the editor (nothing reads it yet); kept when stored or imported. */
  difficulty: z.literal(MATH_EXERCISE_DIFFICULTIES, { error: 'Difficulty must be 1, 2 or 3.' }).optional(),
}

const MathChoiceOption = z.object({ id: z.string(), text: requiredText('The option has no text.') })

const MathPair = z.object({
  id: z.string(),
  left: requiredText('The left side is empty.'),
  right: requiredText('The right side is empty.'),
})

const MathOrderItem = z.object({ id: z.string(), text: requiredText('The item has no text.') })

const MathTick = z.object({ id: z.string(), label: requiredText('The tick has no label.') })

const MathStep = z.object({
  id: z.string(),
  label: requiredText('The step has no label.'),
  answer: typeableAnswer,
})

const MathMultipleChoiceExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.MultipleChoice),
  options: z.array(MathChoiceOption)
    .min(MATH_CHOICE_MIN_OPTIONS, `A multiple choice exercise needs at least ${MATH_CHOICE_MIN_OPTIONS} options.`),
  correctOptionId: z.string({ error: CORRECT_OPTION_MESSAGE }),
})

const MathNumericExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.Numeric),
  answer: typeableAnswer,
})

/** Stored aligned: `pairs[i].left` matches `pairs[i].right`. */
const MathMatchPairsExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.MatchPairs),
  pairs: z.array(MathPair).min(MATH_PAIRS_MIN, `A match pairs exercise needs at least ${MATH_PAIRS_MIN} pairs.`),
})

/** Stored in the correct order. */
const MathOrderExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.Order),
  items: z.array(MathOrderItem)
    .min(MATH_ORDER_MIN_ITEMS, `An order exercise needs at least ${MATH_ORDER_MIN_ITEMS} items.`),
})

const MathNumberLineExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.NumberLine),
  ticks: z.array(MathTick)
    .min(MATH_NUMBER_LINE_MIN_TICKS, `A number line needs at least ${MATH_NUMBER_LINE_MIN_TICKS} ticks.`),
  correctTickId: z.string({ error: CORRECT_TICK_MESSAGE }),
})

const MathMultiStepExercise = z.object({
  ...ExerciseBase,
  type: z.literal(MathExerciseType.MultiStep),
  steps: z.array(MathStep)
    .min(MATH_MULTI_STEP_MIN_STEPS, `A multi-step exercise needs at least ${MATH_MULTI_STEP_MIN_STEPS} steps.`)
    .max(MATH_MULTI_STEP_MAX_STEPS, `A multi-step exercise has at most ${MATH_MULTI_STEP_MAX_STEPS} steps.`),
})

export const MathExercise = z.discriminatedUnion('type', [
  MathMultipleChoiceExercise,
  MathNumericExercise,
  MathMatchPairsExercise,
  MathOrderExercise,
  MathNumberLineExercise,
  MathMultiStepExercise,
], { error: 'Pick an exercise type.' })

const MathPracticeDefinitionObject = z.object({
  id: z.string(),
  schemaVersion: z.number().int(),
  /** The kid-facing set name — not the row's catalogue `title`. */
  title: z.string(),
  description: z.string().optional(),
  disciplineCode: z.literal('MATH'),
  grade: z.enum(Grade),
  exercises: z.array(MathExercise).min(1, 'A math homework needs at least one exercise.'),
})

type MathPracticeDefinitionShape = z.output<typeof MathPracticeDefinitionObject>
type MathExerciseShape = z.output<typeof MathExercise>

type IssuePath = (string | number)[]
type AddIssue = (issue: { code: 'custom', message: string, path: IssuePath }) => void

/** Same rule as the reading schema: an exact match, since there is no upcaster either way. */
const refineSchemaVersion = (definition: MathPracticeDefinitionShape, addIssue: AddIssue): void => {
  if (definition.schemaVersion === MATH_PRACTICE_SCHEMA_VERSION) {
    return
  }
  addIssue({
    code: 'custom',
    path: ['schemaVersion'],
    message: definition.schemaVersion > MATH_PRACTICE_SCHEMA_VERSION
      ? `Math practice schema version ${definition.schemaVersion} is newer than this editor supports (${MATH_PRACTICE_SCHEMA_VERSION}). Update app-admin before editing this set.`
      : `Math practice schema version ${definition.schemaVersion} is older than this editor supports (${MATH_PRACTICE_SCHEMA_VERSION}). This editor has no upcaster.`,
  })
}

/** Reported on every repeat occurrence, at `<path>.<i>.<field>`. Empty values are skipped: their own
 *  field already reports them, and two blanks are not a duplicate the author needs told about. */
const refineUnique = <T>(
  list: T[],
  valueOf: (item: T) => string,
  path: IssuePath,
  field: string,
  message: (value: string) => string,
  addIssue: AddIssue,
): void => {
  const seen = new Set<string>()
  list.forEach((item, index) => {
    const value = valueOf(item)
    if (value === '') {
      return
    }
    if (seen.has(value)) {
      addIssue({ code: 'custom', path: [...path, index, field], message: message(value) })
      return
    }
    seen.add(value)
  })
}

const uniqueIdMessage = (noun: string) => (id: string): string =>
  `Duplicate ${noun} id "${id}" — ${noun} ids must be unique within the exercise.`

const refineReference = (
  ids: string[],
  selected: string,
  path: IssuePath,
  missing: string,
  addIssue: AddIssue,
): void => {
  if (!selected) {
    addIssue({ code: 'custom', path, message: missing })
    return
  }
  if (!ids.includes(selected)) {
    addIssue({ code: 'custom', path, message: 'The marked answer no longer exists. Mark it again.' })
  }
}

const refineExercise = (exercise: MathExerciseShape, at: IssuePath, addIssue: AddIssue): void => {
  switch (exercise.type) {
    case MathExerciseType.MultipleChoice:
      refineUnique(exercise.options, option => option.id, [...at, 'options'], 'id', uniqueIdMessage('option'), addIssue)
      refineReference(
        exercise.options.map(option => option.id),
        exercise.correctOptionId,
        [...at, 'correctOptionId'],
        CORRECT_OPTION_MESSAGE,
        addIssue,
      )
      return
    case MathExerciseType.Numeric:
      return
    case MathExerciseType.MatchPairs:
      refineUnique(exercise.pairs, pair => pair.id, [...at, 'pairs'], 'id', uniqueIdMessage('pair'), addIssue)
      // The player accepts any card carrying the keyed label, so two equal right sides make the
      // key unplayable rather than merely ambiguous.
      refineUnique(
        exercise.pairs,
        pair => pair.right.trim(),
        [...at, 'pairs'],
        'right',
        value => `Two pairs share the right side "${value}" — right sides must be unique.`,
        addIssue,
      )
      return
    case MathExerciseType.Order:
      refineUnique(exercise.items, item => item.id, [...at, 'items'], 'id', uniqueIdMessage('item'), addIssue)
      return
    case MathExerciseType.NumberLine:
      refineUnique(exercise.ticks, tick => tick.id, [...at, 'ticks'], 'id', uniqueIdMessage('tick'), addIssue)
      refineReference(
        exercise.ticks.map(tick => tick.id),
        exercise.correctTickId,
        [...at, 'correctTickId'],
        CORRECT_TICK_MESSAGE,
        addIssue,
      )
      return
    case MathExerciseType.MultiStep:
      refineUnique(exercise.steps, step => step.id, [...at, 'steps'], 'id', uniqueIdMessage('step'), addIssue)
      return
    default: {
      const unreachable: never = exercise
      return unreachable
    }
  }
}

const refineDefinition = (definition: MathPracticeDefinitionShape, addIssue: AddIssue): void => {
  refineSchemaVersion(definition, addIssue)
  refineUnique(
    definition.exercises,
    exercise => exercise.id,
    ['exercises'],
    'id',
    id => `Duplicate exercise id "${id}" — exercise ids must be unique.`,
    addIssue,
  )
  definition.exercises.forEach((exercise, index) => {
    refineExercise(exercise, ['exercises', index], addIssue)
  })
}

export const MathPracticeDefinition = MathPracticeDefinitionObject.superRefine((definition, ctx) => {
  refineDefinition(definition, issue => ctx.addIssue(issue))
})

/** `data` as stored: `{ definition, media? }` — the same envelope as reading, so the backend's title
 *  seeding, duplicate and media signing work unchanged. */
export const MathPracticeDocumentSchema = z.object({
  definition: MathPracticeDefinition,
  /** Manifest of object paths; the store is its only writer. */
  media: z.array(z.string()).optional(),
})

export type ParsedMathExercise = z.output<typeof MathExercise>
export type ParsedMathPracticeDocument = z.output<typeof MathPracticeDocumentSchema>
